import Analytics from './Analytics';
import { type ProductSlugs } from './constants';

export enum AxeAccountAmplitudeEvents {
  LOGIN = 'login',
  VIEW_PAYMENT_PAGE = 'conversion:viewpayment',
  VIEW_LICENSE_AGREEMENT = 'conversion:license',
  VIEW_PRIVACY_POLICY = 'conversion:privacy',
  VIEW_PRODUCTS_PAGE = 'page-view:products',
  VIEW_BILLING_PAGE = 'page-view:billing',
  VIEW_USER_ACCESS_PAGE = 'page-view:user-access',
  VIEW_API_KEYS_PAGE = 'page-view:api-keys',
  VIEW_CONFIGURATION_GLOBAL_PAGE = 'page-view:configuration:global',
  VIEW_CONFIGURATION_INTEGRATIONS_PAGE = 'page-view:configuration:integrations',
  VIEW_INTEGRATIONS_TEMPLATES_PAGE = 'page-view:integrations:templates',
  VIEW_INTEGRATIONS_TEMPLATE_PAGE = 'page-view:integrations:template',
  VIEW_ACCOUNT_SETTINGS_GENERAL_PAGE = 'page-view:account-settings:general',
  VIEW_ACCOUNT_SETTINGS_PASSWORD_PAGE = 'page-view:account-settings:password',
  MOBILE_RULE_CONFIG_VIEWED = 'mobileRuleConfig:view',
  MOBILE_RULE_CONFIG_SAVED = 'mobileRuleConfig:save'
}

export type DynamicEvents =
  | `configuration:${string}:submit`
  | `configuration:${string}:discardForm`
  | `page-view:configuration:${string}`
  | `page-view:integrations:${string}:settings`;

type ConfigTab = 'global' | ProductSlugs;

export type EventData = Record<string, string | boolean | number>;

export default class AxeAccountAnalytics extends Analytics<
  AxeAccountAmplitudeEvents | DynamicEvents
> {
  public login() {
    return this.sendAmplitudeEvent({ event: AxeAccountAmplitudeEvents.LOGIN });
  }

  public viewPaymentPage() {
    return this.sendAmplitudeEvent({
      event: AxeAccountAmplitudeEvents.VIEW_PAYMENT_PAGE
    });
  }

  public viewLicenseAgreement() {
    return this.sendAmplitudeEvent({
      event: AxeAccountAmplitudeEvents.VIEW_LICENSE_AGREEMENT
    });
  }

  public viewPrivacyPolicy() {
    return this.sendAmplitudeEvent({
      event: AxeAccountAmplitudeEvents.VIEW_PRIVACY_POLICY
    });
  }

  public viewProductsPage() {
    return this.sendAmplitudeEvent({
      event: AxeAccountAmplitudeEvents.VIEW_PRODUCTS_PAGE
    });
  }

  public viewBillingPage() {
    return this.sendAmplitudeEvent({
      event: AxeAccountAmplitudeEvents.VIEW_BILLING_PAGE
    });
  }

  public viewUserAccessPage() {
    return this.sendAmplitudeEvent({
      event: AxeAccountAmplitudeEvents.VIEW_USER_ACCESS_PAGE
    });
  }

  public viewApiKeysPage() {
    return this.sendAmplitudeEvent({
      event: AxeAccountAmplitudeEvents.VIEW_API_KEYS_PAGE
    });
  }

  public viewConfigurationGlobalPage(isAdmin: boolean) {
    return this.sendAmplitudeEvent({
      event: AxeAccountAmplitudeEvents.VIEW_CONFIGURATION_GLOBAL_PAGE,
      data: { isAdmin }
    });
  }

  public viewConfigurationProductPage(
    productSlug: ProductSlugs,
    isAdmin: boolean
  ) {
    return this.sendAmplitudeEvent({
      event: `page-view:configuration:${productSlug}`,
      data: { isAdmin }
    });
  }

  public viewConfigurationIntegrationsPage(isAdmin: boolean) {
    return this.sendAmplitudeEvent({
      event: AxeAccountAmplitudeEvents.VIEW_CONFIGURATION_INTEGRATIONS_PAGE,
      data: { isAdmin }
    });
  }

  public viewIntegrationsTemplatesPage() {
    return this.sendAmplitudeEvent({
      event: AxeAccountAmplitudeEvents.VIEW_INTEGRATIONS_TEMPLATES_PAGE
    });
  }

  public viewIntegrationsTemplatePage() {
    return this.sendAmplitudeEvent({
      event: AxeAccountAmplitudeEvents.VIEW_INTEGRATIONS_TEMPLATE_PAGE
    });
  }

  public viewIntegrationsSettingsPage(productSlug: ProductSlugs) {
    return this.sendAmplitudeEvent({
      event: `page-view:integrations:${productSlug}:settings`
    });
  }

  public viewAccountSettingsGeneralPage() {
    return this.sendAmplitudeEvent({
      event: AxeAccountAmplitudeEvents.VIEW_ACCOUNT_SETTINGS_GENERAL_PAGE
    });
  }

  public viewAccountSettingsPasswordPage() {
    return this.sendAmplitudeEvent({
      event: AxeAccountAmplitudeEvents.VIEW_ACCOUNT_SETTINGS_PASSWORD_PAGE
    });
  }

  public configSubmit(tab: ConfigTab, data: EventData) {
    return this.sendAmplitudeEvent({
      event: `configuration:${tab}:submit`,
      data
    });
  }

  public configDiscard(tab: ConfigTab) {
    return this.sendAmplitudeEvent({
      event: `configuration:${tab}:discardForm`
    });
  }

  public mobileRuleConfigViewed(data: { role: 'admin' | 'member' }) {
    return this.sendAmplitudeEvent({
      event: AxeAccountAmplitudeEvents.MOBILE_RULE_CONFIG_VIEWED,
      data
    });
  }

  public mobileRuleConfigSaved(data: {
    rules_disabled_count_ios: number;
    rules_disabled_count_android: number;
    allow_overrides: boolean;
    role: 'admin' | 'member';
  }) {
    return this.sendAmplitudeEvent({
      event: AxeAccountAmplitudeEvents.MOBILE_RULE_CONFIG_SAVED,
      data
    });
  }
}
